import { inject, Injectable } from '@angular/core';
import type { OrderHistoryRecordModel } from '@libs/services/models';
import { OrderStatusModel } from '@libs/shared/models';
import { OrderHistoryNavigationService } from './order-history-navigation.service';

export enum ColumnLabels {
  ID_INVOICE_NR = 'Rechnungsnummer',
  ID_CUSTOMER_ORDER_NR = 'Rechnungs- & ind. Bestellnr.',
  CUSTOMER_ORDER_NR = 'ind. Bestellnummer',
  ORDER_TYPE = 'Bestellkanal',
  ORDER_DATE = 'Bestelldatum',
  ORDER_TIME = 'Bestellzeit',
  ORDER_STATUS = 'Status',
  ORDER_STATUS_UNKNOWN = 'unbekannt',
  ORDER_STATUS_PENDING = 'in Bearbeitung',
  ORDER_STATUS_CANCELLED = 'abgesagt',
  ORDER_STATUS_COMPLETED = 'an Versand übergeben',
  DELIVERY_INFO = 'Lieferinformationen',
  POSITIONS = 'Positionen',
  ACTIONS = 'Aktionen',
  GROSS = 'Bruttowert',
  NET = 'Nettowert',
  NOT_SPECIFIED = 'k.A.',
  POSITIONS_LIMITED_AVAILABLE = 'begrenzt verfügbar',
  POSITIONS_NOT_AVAILABLE = 'nicht verfügbar',
  SHOW_ORDER_RECORD = 'Bestellung ansehen',
  AVAILABILITY_CHECK_INFO = 'Bitte beachten Sie: Eine abschließende Verfügbarkeitsprüfung findet erst am Tag der Lieferung statt.',
}

export const OrderTypes: Record<string, string> = {
  TESP: 'Telefonverkauf',
  MDEF: 'mobile Bestellung durch Fachberater',
  MDEK: 'mobile Bestellung durch Kunde',
  MAPP: 'App',
  APP2: 'App',
  MDEL: 'Bestellung bei Industrie',
  CCDI: 'Kassensystem',
  WEB: 'Webshop',
  WEB2: 'Webshop',
  MDUK: 'Datenfernübertragung Kunde',
  MDFX: 'Fax',
  FAX: 'Telefax',
  PUSH: 'Pushorder',
  MAIL: 'E-Mail',
  DFUE: 'Datenfernübertragung',
  INT: 'Interner Auftrag',
  SCHR: 'Schriftlich',
  'über Telefonverkauf': 'per Telefon',
};

@Injectable({ providedIn: 'root' })
export class OrderHistoryHelperService {
  public readonly ColumnLabels: typeof ColumnLabels = ColumnLabels;
  public readonly OrderTypes: typeof OrderTypes = OrderTypes;

  private readonly navigation: OrderHistoryNavigationService = inject(OrderHistoryNavigationService);

  // SAP only assigns the invoice number once the order is invoiced (`invoice_number` is empty otherwise even for
  // orders already reported as `completed`), so the SAP document number stays the fallback
  public invoiceNumber(order: OrderHistoryRecordModel): string {
    return order.status?.invoiceNumber ?? order.id;
  }

  public orderNumber(order: OrderHistoryRecordModel): string {
    if (order.type === 'WEB2') {
      if (order.customerOrderNumber) {
        return order.customerOrderNumber;
      } else if (order.orderNumber) {
        return order.orderNumber;
      } else {
        return '';
      }
    }

    return order.customerOrderNumber?.toString() ?? '';
  }

  public translateOrderStatus(orderStatus: OrderStatusModel | undefined): string {
    switch (orderStatus?.status) {
      case 'pending':
      case 'in progress':
        return ColumnLabels.ORDER_STATUS_PENDING;
      case 'completed':
        return ColumnLabels.ORDER_STATUS_COMPLETED;
      case 'cancelled':
        return ColumnLabels.ORDER_STATUS_CANCELLED;
      default:
        return ColumnLabels.ORDER_STATUS_UNKNOWN;
    }
  }

  public orderStatusStyle(orderStatus: OrderStatusModel | undefined): string {
    return orderStatus?.status?.replace(/\s+/g, '-').toLowerCase() ?? '';
  }

  public orderLink(order: OrderHistoryRecordModel): string {
    return this.navigation.orderLink(order.id);
  }
}
